/**
 * The board's own feedback button: the same `/feedback.js` a client's site
 * loads (web/public/feedback.js), mounted by hand with a `send` of its own. A
 * client's goes to `POST /api/feedback/:key`; ours needs no key, since the
 * reader is signed in — `POST /api/feedback` (`board.feedbackTask()`) makes a
 * root task on the board on screen, filed under this app's own repo, titled and
 * worded as a client's is, and queues it for Claude as a client's is.
 *
 * It starts bottom-left, where nothing else of ours sits (the git panel's search
 * is bottom-right on a phone), and round rather than a labelled pill. It is over
 * every screen, so wherever it sits it is over something: the widget lets it be
 * dragged to any edge, and keeps where it was left per browser.
 */
import { onMounted, onUnmounted } from "vue";
import { useBoard } from "../stores/board.js";
import { SERVICE } from "@shared/types.js";
import { frontKey, windows, windowTitle } from "./windows.js";

interface Widget {
  destroy(): void;
}
interface FeedbackGlobal {
  mount(opts: { send: (text: string, url: string, title: string, email: string, fields: Record<string, string>) => Promise<void>; corner: string; label: string; compact: boolean; fields?: Record<string, string> | (() => Record<string, string>) }): Widget;
}
declare global {
  interface Window {
    TodosFeedback?: FeedbackGlobal;
  }
}

declare const __TODOS_BUILD__: string;

let loading: Promise<FeedbackGlobal> | null = null;

function load(): Promise<FeedbackGlobal> {
  if (window.TodosFeedback) return Promise.resolve(window.TodosFeedback);
  return (loading ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${import.meta.env.BASE_URL}feedback.js`;
    s.async = true;
    s.onload = () => (window.TodosFeedback ? resolve(window.TodosFeedback) : reject(new Error("feedback.js did not load")));
    s.onerror = () => {
      loading = null;
      reject(new Error("feedback.js did not load"));
    };
    document.head.appendChild(s);
  }));
}

export function useFeedback(): void {
  // The web tests run with no server to load it from.
  if (import.meta.env.MODE === "test") return;
  const board = useBoard();
  let widget: Widget | null = null;
  let gone = false;
  onMounted(() => {
    load().then(
      (fb) => {
        if (gone) return;
        widget = fb.mount({
          corner: "bottom-left",
          label: "Feedback",
          compact: true,
          // The build it was sent from, so a report can be read against the right code, and what the
          // sender had on screen — read at each send, so it is the moment they pressed Send.
          fields: () => {
            const me = board.me;
            const front = windows.value.find((w) => w.key === frontKey.value);
            return {
              build: __TODOS_BUILD__,
              user: me ? `${me.displayName} (${me.username})` : "",
              team: board.personalView ? "Personal" : (board.team?.name ?? ""),
              window: front ? `${front.data.kind}: ${windowTitle(front.data)}` : "",
              screen: `${window.screen.width}×${window.screen.height}, window ${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio}x`,
              agent: navigator.userAgent,
            };
          },
          send: async (text, url, title, _email, fields) => {
            await board.feedbackTask(text, url, SERVICE, title, fields);
          },
        });
      },
      (err: unknown) => console.error(err),
    );
  });
  onUnmounted(() => {
    gone = true;
    widget?.destroy();
    widget = null;
  });
}
